import React from 'react'

import { StatusCounts } from '../../hooks/useStatusCounts'

interface QueueFiltersProps {
  selectedStatus: string
  onStatusChange: (status: string) => void
  selectedTags: string[]
  onTagsChange: (tags: string[]) => void
  selectedPriority: string
  onPriorityChange: (priority: string) => void
  statusCounts?: StatusCounts
}

const AVAILABLE_TAGS = ['billing', 'technical', 'feature-request', 'account', 'integration', 'performance']
const PRIORITIES = [
  { value: '', label: 'All priorities' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
]
// 'missed' is its own top-level Chats tab now (Lee, Oct 2026), so it's no longer
// a status sub-filter here.
const STATUSES = [
  { value: 'queued', label: 'Queued' },
  { value: 'open', label: 'Open' },
  { value: 'auto_closed', label: 'Auto Closed' },
  { value: 'closed', label: 'Closed' },
  { value: 'archived', label: 'Archived' },
]

const countKeyMap: Record<string, keyof StatusCounts> = {
  'queued': 'queued',
  'open': 'open',
  'missed': 'missed',
  'auto_closed': 'auto_closed',
  'closed': 'closed',
  'archived': 'archived',
}

export const QueueFilters: React.FC<QueueFiltersProps> = ({
  selectedStatus,
  onStatusChange,
  selectedTags,
  onTagsChange,
  selectedPriority,
  onPriorityChange,
  statusCounts,
}) => {
  const toggleTag = (tag: string) => {
    if (selectedTags.includes(tag)) {
      onTagsChange(selectedTags.filter((t) => t !== tag))
    } else {
      onTagsChange([...selectedTags, tag])
    }
  }

  const activeFilters = (selectedPriority ? 1 : 0) + selectedTags.length

  return (
    <div className="space-y-4">
      {/* Status tabs */}
      <div className="flex items-center gap-1 border-b border-border overflow-x-auto">
        {STATUSES.map((s) => {
          const countKey = countKeyMap[s.value]
          const count = statusCounts ? statusCounts[countKey] : undefined
          return (
            <button
              key={s.value}
              onClick={() => onStatusChange(s.value)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors duration-150 ${
                selectedStatus === s.value
                  ? 'border-brand-600 text-brand-600 dark:text-brand-400 dark:border-brand-400'
                  : 'border-transparent text-txt-secondary hover:text-txt-primary hover:border-border-strong'
              }`}
            >
              {s.label}
              {count !== undefined && count > 0 && (
                <span
                  className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold ${
                    selectedStatus === s.value
                      ? 'bg-brand-600 text-white dark:bg-brand-500'
                      : 'bg-surface-tertiary text-txt-secondary dark:bg-white/10'
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Filter row */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Priority dropdown */}
        <select
          value={selectedPriority}
          onChange={(e) => onPriorityChange(e.target.value)}
          className="px-3 py-1.5 rounded-lg bg-surface border border-border text-sm text-txt-primary outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 transition-colors"
        >
          {PRIORITIES.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>

        {/* Divider so the dropdown and tag pills don't visually collide */}
        <span className="hidden sm:block w-px h-5 bg-border" />

        {/* Tag pills */}
        <div className="flex items-center gap-2 flex-wrap">
          {AVAILABLE_TAGS.map((tag) => (
            <button
              key={tag}
              onClick={() => toggleTag(tag)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all duration-150 ${
                selectedTags.includes(tag)
                  ? 'bg-brand-50 text-brand-700 border-brand-200 dark:bg-brand-900/30 dark:text-brand-300 dark:border-brand-700'
                  : 'bg-surface text-txt-secondary border-border hover:border-border-strong hover:text-txt-primary'
              }`}
            >
              {tag}
            </button>
          ))}
        </div>

        {/* Clear filters */}
        {activeFilters > 0 && (
          <button
            onClick={() => {
              onPriorityChange('')
              onTagsChange([])
            }}
            className="ml-auto px-2.5 py-1 rounded-md text-xs font-medium text-txt-secondary hover:text-txt-primary hover:bg-surface-hover transition-colors"
          >
            Clear {activeFilters} filter{activeFilters > 1 ? 's' : ''}
          </button>
        )}
      </div>
    </div>
  )
}
